import { useState, useEffect } from "react";
import { useCardExpand } from "../../lib/cardExpand";
import { ExpandButton } from "./CardModal";

type Mode = "ywi" | "recovery" | "consistency" | "quality";

type Props = {
  ywi: any;
  vitals: any;  // last wearable_daily snapshot for recovery/sleep
  plan?: any;   // LifeScribe rules-based focus recommendation
  _expanded?: boolean;  // true when rendered inside the expand modal
};

export function InsightCard({ ywi, vitals, plan, _expanded }: Props) {
  const { expand } = useCardExpand();
  const [mode, setMode] = useState<Mode>("ywi");
  const [animatedValue, setAnimatedValue] = useState(0);

  // Dimension display meta for the LifeScribe plan
  const DIM_META: Record<string, { color: string; icon: string; label: string }> = {
    physical:  { color: "#2A9D8F", icon: "💪", label: "Physical"  },
    emotional: { color: "#E76F51", icon: "🧡", label: "Emotional" },
    mental:    { color: "#457B9D", icon: "🧠", label: "Mental"    },
    spiritual: { color: "#F4A261", icon: "✨", label: "Spiritual" },
    social:    { color: "#E9C46A", icon: "🤝", label: "Social"    },
  };
  const planFocus = plan?.focus ? DIM_META[plan.focus] : null;
  const intensityLabel: Record<string, string> = { restore: "Restore", maintain: "Maintain", build: "Build" };

  // Per-mode values (computed from real data when available)
  const recoveryVal     = vitals?.recovery_score ? Math.round(vitals.recovery_score) : 78;
  const consistencyVal  = ywi?.consistency_score ?? 65;
  const qualityVal      = vitals?.sleep_score ? Math.round(vitals.sleep_score) : 72;
  const ywiVal          = ywi?.ywi_total ? Math.round(ywi.ywi_total) : 0;

  const modeData: Record<Mode, { val: number; color: string | null; label: string; hint: string }> = {
    ywi:         { val: ywiVal,         color: null,     label: "YWI Score™",   hint: "Composite of all 5 dimensions." },
    recovery:    { val: recoveryVal,    color: "#2A9D8F",label: "Recovery",     hint: "From HRV + RHR + sleep. Higher = more resilient." },
    consistency: { val: consistencyVal, color: "#A8DADC",label: "Consistency",  hint: "% of last 30 days with activity logged." },
    quality:     { val: qualityVal,     color: "#F4A261",label: "Quality",      hint: "Sleep score blend — duration, deep%, REM%." },
  };

  const current = modeData[mode];

  // Animate to current value when mode changes
  useEffect(() => {
    let from = animatedValue;
    let to = current.val;
    if (from === to) return;
    const dur = 700;
    const start = performance.now();
    let raf: number;
    const step = (t: number) => {
      const p = Math.min(1, (t - start) / dur);
      const eased = 1 - Math.pow(1 - p, 3);
      setAnimatedValue(Math.round(from + (to - from) * eased));
      if (p < 1) raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, current.val]);

  // Compute SVG arc props
  const radius = 52;
  const circ = 2 * Math.PI * radius;
  const pct = Math.max(0, Math.min(100, animatedValue)) / 100;
  const offset = circ - pct * circ;
  const stroke = current.color ?? "url(#ywiGrad)";
  const bgStroke = current.color ? `${current.color}18` : "rgba(42,157,143,.06)";

  return (
    <div className={_expanded ? "flex flex-col h-full" : "bg-d_card border border-d_border/20 rounded-[10px] p-3 transition-colors hover:border-d_celeste/10 h-full min-h-0 overflow-hidden flex flex-col"}>
      {!_expanded && (
        <div className="font-serif text-[17px] font-semibold mb-2.5 flex items-center justify-between tracking-[0.01em]">
          Daily Insight <ExpandButton onClick={() => expand({ title: "Daily Insight", render: () => <InsightCard ywi={ywi} vitals={vitals} plan={plan} _expanded /> })} />
        </div>
      )}

      <div className="flex gap-4 mb-2 items-start">
        {/* Multi-mode gauge */}
        <div className="w-[124px] h-[124px] flex-shrink-0 relative">
          <svg viewBox="-60 -60 120 120">
            <defs>
              <linearGradient id="ywiGrad" x1="0" y1="0" x2="1" y2="1">
                <stop offset="0%" stopColor="#2A9D8F" />
                <stop offset="100%" stopColor="#A8DADC" />
              </linearGradient>
            </defs>
            {/* Tick marks */}
            <g>
              {Array.from({ length: 60 }, (_, i) => {
                const a = (i / 60) * 360;
                const len = i % 5 === 0 ? 5 : 2;
                const r2 = 52 - len;
                const x1 = Math.cos((a * Math.PI) / 180) * 52;
                const y1 = Math.sin((a * Math.PI) / 180) * 52;
                const x2 = Math.cos((a * Math.PI) / 180) * r2;
                const y2 = Math.sin((a * Math.PI) / 180) * r2;
                return <line key={i} x1={x1} y1={y1} x2={x2} y2={y2} style={{ stroke: "rgb(var(--text3) / 0.22)" }} strokeWidth={i % 5 === 0 ? 0.8 : 0.4} />;
              })}
            </g>
            <circle r={radius} fill="none" stroke={bgStroke} strokeWidth={6} />
            <circle
              r={radius}
              fill="none"
              stroke={stroke}
              strokeWidth={6}
              strokeLinecap="round"
              strokeDasharray={circ}
              strokeDashoffset={offset}
              transform="rotate(-90)"
              style={{ transition: "stroke-dashoffset 0.8s ease" }}
            />
          </svg>
          <div className="absolute inset-0 flex flex-col items-center justify-center">
            <div className="font-serif text-[42px] font-bold leading-none text-d_text" style={current.color ? { color: current.color } : undefined}>
              {animatedValue}
            </div>
            <div className="text-[9px] tracking-[2px] uppercase mt-1.5 font-bold text-d_text2" style={current.color ? { color: current.color } : undefined}>
              {current.label}
            </div>
          </div>
        </div>

        {/* Mode switcher + recommendation */}
        <div className="flex-1 flex flex-col">
          <div className="flex gap-2 mb-2.5">
            {(["ywi","recovery","consistency","quality"] as Mode[]).map((m) => (
              <button
                key={m}
                onClick={() => setMode(m)}
                className={`text-center px-3 py-2.5 rounded-lg border transition flex-1 ${
                  mode === m ? "border-d_accent/60 bg-d_accent/[.08] shadow-[0_2px_8px_rgba(42,157,143,0.18)]" : "border-d_border/20 hover:border-d_accent/30 hover:bg-d_accent/[.03]"
                }`}
              >
                <div className="font-serif text-[26px] font-bold leading-tight tabular-nums text-d_text" style={mode === m && modeData[m].color ? { color: modeData[m].color! } : undefined}>
                  {modeData[m].val}{m === "consistency" ? <span className="text-[18px] font-sans font-semibold ml-0.5 opacity-90">%</span> : null}
                </div>
                <div className="text-[10px] tracking-[1.5px] uppercase mt-1 text-d_text2 font-semibold">
                  {m === "ywi" ? "YWI" : m.charAt(0).toUpperCase() + m.slice(1)}
                </div>
              </button>
            ))}
          </div>
          <div className="bg-d_accent/[.05] border border-d_accent/[.12] rounded-lg px-3 py-2 text-[11px] text-d_text2 leading-[1.5]">
            <strong className="text-d_text">{current.label}:</strong> {current.hint}
          </div>
        </div>
      </div>

      {/* ─── LifeScribe Plan: rules-based focus recommendation from real wearable signals ─── */}
      {plan && planFocus && (
        <div className="mt-2 rounded-xl border border-d_border/30 overflow-hidden flex-1 min-h-0 flex flex-col">
          {/* Header strip — focus dimension + intensity */}
          <div
            className="flex items-center gap-2.5 px-3.5 py-2.5"
            style={{ background: `linear-gradient(135deg, ${planFocus.color}22, ${planFocus.color}08)` }}
          >
            <div
              className="w-9 h-9 rounded-lg flex items-center justify-center text-xl flex-shrink-0"
              style={{ background: `${planFocus.color}22` }}
            >
              {planFocus.icon}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <span className="text-[9px] tracking-[2px] uppercase font-bold" style={{ color: planFocus.color }}>
                  LifeScribe Plan
                </span>
                <span className="text-[8px] tracking-[1.5px] uppercase font-semibold px-1.5 py-0.5 rounded-full bg-d_text/[.06] text-d_text2">
                  {intensityLabel[plan.intensity] ?? plan.intensity}
                </span>
              </div>
              <div className="font-serif text-[16px] font-semibold leading-tight text-d_text mt-0.5">
                {plan.headline}
              </div>
            </div>
            <div className="text-right flex-shrink-0">
              <div className="text-[8px] tracking-[1.5px] uppercase text-d_text3 font-semibold">Focus</div>
              <div className="font-serif text-[15px] font-bold leading-none" style={{ color: planFocus.color }}>
                {planFocus.label}
              </div>
            </div>
          </div>

          {/* Reason + recommended sessions */}
          <div className="px-3.5 py-2.5 flex-1 min-h-0 overflow-y-auto">
            <p className="text-[12px] text-d_text2 leading-[1.55]">{plan.reason}</p>
            {Array.isArray(plan.sessions) && plan.sessions.length > 0 && (
              <div className="mt-2.5">
                <div className="text-[8px] tracking-[1.8px] uppercase text-d_text3 font-semibold mb-1.5">Recommended sessions</div>
                <div className="flex flex-wrap gap-1.5">
                  {plan.sessions.map((s: string) => (
                    <span
                      key={s}
                      className="text-[11px] font-medium px-2.5 py-1 rounded-full border"
                      style={{ borderColor: `${planFocus.color}40`, background: `${planFocus.color}12`, color: planFocus.color }}
                    >
                      {s}
                    </span>
                  ))}
                </div>
              </div>
            )}
            {plan.confidence === "low" && (
              <div className="mt-2 text-[10px] text-d_text3 italic">
                Connect a wearable to sharpen this recommendation.
              </div>
            )}
          </div>
        </div>
      )}

    </div>
  );
}
